import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import axios from 'axios'
import { useAuthStore } from './auth'
import { useNotificationStore } from './notifications'
import { usePlayerStore } from './player'
import { useSessionStore } from './session'
import { useMapStore } from './map'
import { useFormatters } from '@/composables/useFormatters'
import { Client } from '@stomp/stompjs'
import SockJS from 'sockjs-client'
import * as planning from '@/map/composables/useEligibility'

function apiClient() {
  const auth = useAuthStore()
  const client = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    headers: auth.accessToken
      ? { Authorization: `Bearer ${auth.accessToken}` }
      : {}
  })
  client.interceptors.response.use(
    res => res,
    async err => {
      if (err.response?.status === 401 && !err.config._retry) {
        err.config._retry = true
        await auth.silentRenew()
        if (auth.accessToken) {
          err.config.headers['Authorization'] = `Bearer ${auth.accessToken}`
          return client(err.config)
        }
      }
      return Promise.reject(err)
    }
  )
  return client
}

export const useScenarioStore = defineStore('scenario', () => {
  const scenarios     = ref([])
  const current       = ref(null)
  const contracts     = ref([])
  const myContracts   = ref([])
  const me            = ref(null)
  const company       = ref(null)
  const fleet         = ref([])
  const finance       = ref(null)
  const transactions  = ref([])
  const balanceHistory = ref([])
  const vehicleTypes  = ref([])
  const homeNodes     = ref([])
  const allNodes      = ref([])
  const allSegments   = ref([])

  const { fmtMoneySign } = useFormatters()

  let stompClient = null
  let intentionalDisconnect = false

  async function fetchScenarios() {
    const { data } = await apiClient().get('/scenarios')
    scenarios.value = data
  }

  async function fetchScenario(id) {
    const { data } = await apiClient().get(`/scenarios/${id}`)
    current.value = data
    useSessionStore().setNameIfSelected(data.id, data.name)
  }

  async function fetchContracts(scenarioId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/contracts`)
    contracts.value = data
  }

  async function fetchMyContracts(scenarioId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/me/contracts`)
    myContracts.value = data
  }

  async function fetchMe(scenarioId) {
    try {
      const { data } = await apiClient().get(`/scenarios/${scenarioId}/me`)
      me.value = data
    } catch (e) {
      if (e.response?.status === 404) {
        me.value = null
      } else {
        throw e
      }
    }
  }

  async function fetchFleet(scenarioId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/me/fleet`)
    fleet.value = data
  }

  async function fetchFinance(scenarioId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/me/finance`)
    finance.value = data
  }

  async function fetchTransactions(scenarioId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/me/finance/transactions`)
    transactions.value = data
  }

  async function fetchBalanceHistory(scenarioId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/me/finance/balance-history`)
    balanceHistory.value = data
  }

  async function fetchVehicleTypes(scenarioId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/vehicle-types`)
    vehicleTypes.value = data
  }

  async function fetchHomeNodes(scenarioId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/map/nodes`)
    homeNodes.value = data.filter(n => n.homeNodeCandidate)
  }

  async function fetchAllNodes(scenarioId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/map/nodes`)
    allNodes.value = data
  }

  async function fetchAllSegments(scenarioId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/map/segments`)
    allSegments.value = data.map(seg => ({
      ...seg,
      waypoints: seg.waypoints ? seg.waypoints.map(([lon, lat]) => [lat, lon]) : null
    }))
  }

  async function joinScenario(scenarioId, homeNodeId) {
    await apiClient().post(`/scenarios/${scenarioId}/join`, { homeNodeId })
  }

  async function createScenario(payload) {
    await apiClient().post('/admin/scenarios', payload)
  }

  async function startScenario(id) {
    await apiClient().post(`/admin/scenarios/${id}/start`)
  }

  async function updateScenario(id, payload) {
    await apiClient().put(`/admin/scenarios/${id}`, payload)
  }

  async function claimContract(scenarioId, contractId) {
    await apiClient().post(`/scenarios/${scenarioId}/me/contracts/${contractId}/claim`)
  }

  async function purchaseVehicle(scenarioId, vehicleTypeId) {
    await apiClient().post(`/scenarios/${scenarioId}/me/fleet`, { vehicleTypeId })
  }

  async function assignVehicle(scenarioId, vehicleId, contractId, acknowledgeMissedDepartures = false) {
    await apiClient().post(`/scenarios/${scenarioId}/me/assignments`,
      { vehicleId, contractId, acknowledgeMissedDepartures })
  }

  async function repositionVehicle(scenarioId, vehicleId, destinationNodeId, acknowledgeMissedDepartures = false) {
    await apiClient().post(`/scenarios/${scenarioId}/me/repositioning`,
      { vehicleId, destinationNodeId, acknowledgeMissedDepartures })
  }

  async function cancelQueuedAssignment(scenarioId, assignmentId) {
    await apiClient().delete(`/scenarios/${scenarioId}/me/assignments/${assignmentId}`)
  }

  async function fetchScenarioPlayers(scenarioId) {
    const { data } = await apiClient().get(`/admin/scenarios/${scenarioId}/players`)
    return data
  }

  async function adjustPlayerBalance(scenarioId, scenarioPlayerId, amount, reason) {
    await apiClient().post(`/admin/scenarios/${scenarioId}/players/${scenarioPlayerId}/balance-adjustment`, { amount, reason })
  }

  async function fetchAdminContracts(scenarioId) {
    const { data } = await apiClient().get(`/admin/scenarios/${scenarioId}/contracts`)
    return data
  }

  async function voidAllContracts(scenarioId) {
    await apiClient().delete(`/admin/scenarios/${scenarioId}/contracts`)
  }

  async function voidSelectedContracts(scenarioId, contractIds) {
    await apiClient().post(`/admin/scenarios/${scenarioId}/contracts/void`, { contractIds })
  }

  async function fetchCompany(scenarioId) {
    try {
      const { data } = await apiClient().get(`/scenarios/${scenarioId}/me/company`)
      company.value = data
    } catch (e) {
      if (e.response?.status === 404) {
        company.value = null
      } else {
        throw e
      }
    }
  }

  async function saveCompany(scenarioId, name) {
    const { data } = await apiClient().put(`/scenarios/${scenarioId}/me/company`, { name })
    company.value = data
  }

  async function fetchAdminTicks(scenarioId, page = 0, size = 20) {
    const { data } = await apiClient().get(`/admin/scenarios/${scenarioId}/ticks`, { params: { page, size } })
    return data
  }

  async function fetchAdminTickContracts(scenarioId, tickId) {
    const { data } = await apiClient().get(`/admin/scenarios/${scenarioId}/ticks/${tickId}/contracts`)
    return data
  }

  async function fetchAdminPlayers() {
    const { data } = await apiClient().get('/admin/players')
    return data
  }

  async function grantTokens(playerId, amount, reason) {
    await apiClient().post(`/admin/players/${playerId}/tokens`, { amount, reason })
  }

  async function fetchAdminAudit(scenarioId, { type, page = 0, size = 50 } = {}) {
    const params = { page, size }
    if (type) params.type = type
    const { data } = await apiClient().get(`/admin/scenarios/${scenarioId}/audit`, { params })
    return data
  }

  async function fetchAdminRouteAudit(scenarioId, { type, page = 0, size = 50 } = {}) {
    const params = { page, size }
    if (type) params.type = type
    const { data } = await apiClient().get(`/admin/scenarios/${scenarioId}/route-audit`, { params })
    return data
  }

  function connectWebSocket(scenarioId, playerId) {
    const notifications = useNotificationStore()
    notifications.setCurrentScenario(scenarioId)
    stompClient = new Client({
      webSocketFactory: () => new SockJS(import.meta.env.VITE_WS_URL),
      reconnectDelay: 5000,
      onConnect: () => {
        stompClient.subscribe(`/topic/scenario/${scenarioId}`, msg => {
          const event = JSON.parse(msg.body)
          handleScenarioEvent(event, scenarioId)
        })
        if (playerId) {
          stompClient.subscribe(`/topic/player/${playerId}`, msg => {
            const event = JSON.parse(msg.body)
            handlePlayerEvent(event, scenarioId)
          })
        }
      },
      onStompError: frame => {
        notifications.push({ type: 'danger', title: 'Connection error', body: frame.headers?.message ?? 'WebSocket STOMP error' })
      },
      onDisconnect: () => {
        if (!intentionalDisconnect) {
          notifications.push({ type: 'warning', title: 'Disconnected', body: 'Lost connection to server — reconnecting…', silent: true })
        }
        intentionalDisconnect = false
      },
    })
    stompClient.activate()
  }

  function disconnectWebSocket() {
    intentionalDisconnect = true
    stompClient?.deactivate()
    useNotificationStore().setCurrentScenario(null)
    // Pulses are not scenario-keyed — they live for seconds and only arrive for
    // the open scenario. Dropping them here stops the last few from blooming on
    // the next scenario's map, where they describe traffic that isn't there.
    useMapStore().clearCorridorPulses()
  }

  function handleScenarioEvent(event, scenarioId) {
    const notifications = useNotificationStore()
    const gameTag = event.scenarioName ? `in ${event.scenarioName}` : null
    if (event.event === 'TICK_COMPLETED') {
      fetchMe(scenarioId)
      fetchContracts(scenarioId)
      fetchMyContracts(scenarioId)
      fetchFleet(scenarioId)
      if (scheduledRoutes.value.length > 0) fetchScheduledRoutes(scenarioId)
      if (me.value?.scheduledRoutesEnabled) {
        fetchCorridorDemand(scenarioId)
        fetchCorridorState(scenarioId)
      }
      if (event.newContracts > 0) {
        const n = event.newContracts
        const s = n === 1 ? '' : 's'
        const body = ['Check them out and try to fit them into your schedule.', gameTag].filter(Boolean).join(' · ')
        notifications.push({ type: 'info', title: `${n} new contract${s} available`, body, scenarioId })
      }
    } else if (event.event === 'CORRIDOR_DEPARTURE') {
      // Ambience, not a message (spec §6): no notification, no refetch. The
      // payload is anonymous by contract — origin, destination and mode only —
      // so there is nothing here to attribute or act on. Owned by the map store
      // because a pulse is view state with a seconds-long life.
      useMapStore().pushCorridorPulse({
        id: `${event.originNodeId}|${event.destinationNodeId}|${event.mode}|${Date.now()}`,
        originNodeId:      event.originNodeId,
        destinationNodeId: event.destinationNodeId,
        mode:              event.mode,
      })
    } else if (event.event === 'CONTRACT_CLAIMED') {
      contracts.value = contracts.value.filter(c => c.id !== event.contractId)
    } else if (event.event === 'SCENARIO_STARTED') {
      const body = ['The game has started. Good luck!', gameTag].filter(Boolean).join(' · ')
      notifications.push({ id: event.notificationId || null, type: 'accent', title: 'Game is live', body, scenarioId })
    } else if (event.event === 'SCENARIO_COMPLETED') {
      const body = ['Final scores are being calculated.', gameTag].filter(Boolean).join(' · ')
      notifications.push({ id: event.notificationId || null, type: 'warning', title: 'Game ended', body, scenarioId })
    } else if (event.event === 'SCENARIO_WARNING') {
      const body = ['Plan your remaining deliveries carefully.', gameTag].filter(Boolean).join(' · ')
      notifications.push({ type: 'warning', title: `${event.gameDaysRemaining} game days remaining`, body, scenarioId })
    }
  }

  function handlePlayerEvent(event, scenarioId) {
    const notifications = useNotificationStore()
    const isCurrentScenario = !event.scenarioId || event.scenarioId === scenarioId
    const gameTag = event.scenarioName ? `in ${event.scenarioName}` : null
    if (event.event === 'CONTRACT_COMPLETED') {
      if (isCurrentScenario) {
        fetchMe(scenarioId)
        fetchMyContracts(scenarioId)
        fetchFleet(scenarioId)
      }
      const payout = event.payout ? fmtMoneySign(event.payout) : null
      const route = event.originName && event.destinationName
        ? `${event.originName} → ${event.destinationName}` : null
      const vehicle = event.vehicleTypeName || null
      const body = [vehicle, route, payout, gameTag].filter(Boolean).join(' · ') || null
      notifications.push({ id: event.notificationId || null, type: 'success', title: 'Delivery complete', body, scenarioId: event.scenarioId })
    } else if (event.event === 'SCHEDULED_DEPARTURE') {
      if (isCurrentScenario) fetchFleet(scenarioId)
      const route = event.originName && event.destinationName
        ? `${event.originName} → ${event.destinationName}` : null
      const body = [route, gameTag].filter(Boolean).join(' · ') || null
      notifications.push({ id: event.notificationId || null, type: 'info', title: 'Scheduled departure', body, silent: true, scenarioId: event.scenarioId })
    } else if (event.event === 'SCHEDULED_ARRIVAL') {
      if (isCurrentScenario) {
        fetchMe(scenarioId)
        fetchFleet(scenarioId)
      }
      const payout = event.revenue ? fmtMoneySign(event.revenue) : null
      const route = event.originName && event.destinationName
        ? `${event.originName} → ${event.destinationName}` : null
      const body = [route, payout, gameTag].filter(Boolean).join(' · ') || null
      notifications.push({ id: event.notificationId || null, type: 'success', title: 'Route delivery complete', body, scenarioId: event.scenarioId })
    } else if (event.event === 'SCHEDULED_DEPARTURE_MISSED') {
      const route = event.originName && event.destinationName
        ? `${event.originName} → ${event.destinationName}` : null
      const detail = route ? `No vehicle at origin for ${route}` : null
      const body = [detail, gameTag].filter(Boolean).join(' · ') || null
      notifications.push({ id: event.notificationId || null, type: 'warning', title: 'Missed departure', body, scenarioId: event.scenarioId })
    } else if (event.event === 'SCHEDULED_DEPARTURE_PREEMPTED') {
      if (isCurrentScenario) fetchFleet(scenarioId)
      const route = event.originName && event.destinationName
        ? `${event.originName} → ${event.destinationName}` : null
      const detail = route ? `${route} preempted by ad-hoc work` : null
      const body = [detail, gameTag].filter(Boolean).join(' · ') || null
      notifications.push({ id: event.notificationId || null, type: 'info', title: 'Scheduled departure cancelled', body, scenarioId: event.scenarioId })
    } else if (event.event === 'SCHEDULED_ROUTE_PAUSED') {
      if (isCurrentScenario) fetchScheduledRoutes(scenarioId)
      const route = event.originName && event.destinationName
        ? `${event.originName} → ${event.destinationName}` : null
      const body = [route, gameTag].filter(Boolean).join(' · ') || null
      notifications.push({ id: event.notificationId || null, type: 'warning', title: 'Scheduled route paused', body, scenarioId: event.scenarioId })
    } else if (event.event === 'SCHEDULED_ROUTE_RESUMED') {
      if (isCurrentScenario) fetchFleet(scenarioId)
      const route = event.originName && event.destinationName
        ? `${event.originName} → ${event.destinationName}` : null
      const body = [route, gameTag].filter(Boolean).join(' · ') || null
      notifications.push({ id: event.notificationId || null, type: 'info', title: 'Scheduled route resumed', body, scenarioId: event.scenarioId })
    } else if (event.event === 'ASSIGNMENT_CANCELLED') {
      if (isCurrentScenario) {
        fetchFleet(scenarioId)
        fetchMyContracts(scenarioId)
      }
      const route = event.originName && event.destinationName
        ? `${event.originName} → ${event.destinationName}` : null
      const detail = route ? `${route} — vehicle was not at origin` : event.reason
      const body = [detail, gameTag].filter(Boolean).join(' · ') || null
      notifications.push({ id: event.notificationId || null, type: 'error', title: 'Assignment cancelled', body, scenarioId: event.scenarioId })
    } else if (event.event === 'TOKEN_BALANCE_CHANGED') {
      usePlayerStore().fetchProfile()
      const amount = event.amount
      if (amount > 0) {
        notifications.push({ type: 'success', title: `${amount} token${amount === 1 ? '' : 's'} added to your account` })
      } else if (amount < 0) {
        notifications.push({ type: 'info', title: 'Daily token deduction', body: `${Math.abs(amount)} token${Math.abs(amount) === 1 ? '' : 's'} deducted` })
      }
    }
  }

  /**
   * Convert a game datetime string (ISO, no timezone) to an estimated real-world Date.
   * Uses proportional interpolation: game-ms remaining / game-ms-per-tick * real-ms-per-tick.
   * Returns null if the clock data isn't available (no ticks yet).
   */
  function gameToRealDate(gameDateTimeStr) {
    const state = me.value
    if (!state?.lastTickProcessedAt || !gameDateTimeStr) return null

    // Parse as UTC (game time has no timezone — treat consistently across all clients)
    const currentMs = new Date(state.currentGameDateTime + 'Z').getTime()
    const targetMs  = new Date(gameDateTimeStr + 'Z').getTime()
    const gameMsUntil = targetMs - currentMs
    if (gameMsUntil <= 0) return new Date(state.lastTickProcessedAt)

    const gameMsPerTick = state.gameDaysPerTick * 86_400_000
    const realMsPerTick = state.tickIntervalMinutes * 60_000
    const realMsUntil = (gameMsUntil / gameMsPerTick) * realMsPerTick

    return new Date(new Date(state.lastTickProcessedAt).getTime() + realMsUntil)
  }

  const vehicleEconomics = ref(null)

  async function fetchVehicleEconomics(scenarioId, vehicleId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/me/vehicles/${vehicleId}/economics`)
    vehicleEconomics.value = data
  }

  // ── Scheduled routes ────────────────────────────────────────────────────────

  const scheduledRoutes = ref([])
  const corridorDemand  = ref([])
  const corridorState   = ref([])

  async function fetchScheduledRoutes(scenarioId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/me/routes`)
    scheduledRoutes.value = data
  }

  async function createScheduledRoute(scenarioId, payload) {
    const { data } = await apiClient().post(`/scenarios/${scenarioId}/me/routes`, payload)
    return data
  }

  async function updateScheduledRoute(scenarioId, routeId, payload) {
    await apiClient().put(`/scenarios/${scenarioId}/me/routes/${routeId}`, payload)
  }

  async function cancelScheduledRoute(scenarioId, routeId) {
    await apiClient().delete(`/scenarios/${scenarioId}/me/routes/${routeId}`)
  }

  async function assignRouteVehicle(scenarioId, routeId, vehicleId) {
    await apiClient().post(`/scenarios/${scenarioId}/me/routes/${routeId}/vehicles`, { vehicleId })
  }

  async function removeRouteVehicle(scenarioId, routeId, vehicleId) {
    await apiClient().delete(`/scenarios/${scenarioId}/me/routes/${routeId}/vehicles/${vehicleId}`)
  }

  async function fetchCorridorDemand(scenarioId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/routes/demand`)
    corridorDemand.value = data
  }

  async function fetchCorridorState(scenarioId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/corridors`)
    corridorState.value = data
  }

  // Corridor inspector detail-on-selection (issue 434). The selection pointer
  // (which node pair is chosen) lives in stores/map.js — that store owns view
  // state, not backend data — but the fetch itself belongs here alongside every
  // other HTTP call in the app, since apiClient() is private to this file.
  const corridorDetail = ref(null)
  const corridorDetailLoading = ref(false)

  async function fetchCorridorDetail(scenarioId, originNodeId, destinationNodeId) {
    corridorDetailLoading.value = true
    try {
      const { data } = await apiClient().get(`/scenarios/${scenarioId}/corridors/${originNodeId}/${destinationNodeId}`)
      corridorDetail.value = data
    } finally {
      corridorDetailLoading.value = false
    }
  }

  function clearCorridorDetail() {
    corridorDetail.value = null
  }

  async function fetchVehicleCalendar(scenarioId, vehicleId) {
    const { data } = await apiClient().get(`/scenarios/${scenarioId}/me/fleet/${vehicleId}/calendar`)
    return data
  }

  // ── Planning mode (map click-to-act) ────────────────────────────────────────
  // Additive only — the side-panel flow and existing getters are untouched (spec §7).

  // Persisted per scenario so returning players land in their last-used mode.
  const planningModeByScenario = useLocalStorage('transigo:planningModeByScenario', {})
  const planningMode = ref(false)
  const openPopoverContext = ref(null)

  // Reflect the active scenario's persisted preference whenever it changes.
  watch(() => current.value?.id, id => {
    planningMode.value = id ? !!planningModeByScenario.value[id] : false
  }, { immediate: true })

  function setPlanningMode(enabled) {
    planningMode.value = enabled
    const id = current.value?.id
    if (id) planningModeByScenario.value = { ...planningModeByScenario.value, [id]: enabled }
  }

  function openPopover(context) { openPopoverContext.value = context }
  function closePopover() { openPopoverContext.value = null }

  // Sibling getters to the existing DashboardView eligibility helpers — see
  // map/composables/useEligibility.js for the (deliberately mirrored) rules.
  const vehicleEligibility = computed(() => (contract, vehicle) =>
    planning.vehicleEligibility(contract, vehicle))

  const repositionableVehiclesTo = computed(() => nodeId =>
    planning.repositionableVehiclesTo(allNodes.value.find(n => n.id === nodeId), fleet.value))

  const reachableContractsFor = computed(() => vehicleId =>
    planning.reachableContractsFor(
      fleet.value.find(v => v.id === vehicleId), contracts.value, myContracts.value, fleet.value))

  const repoDestNodesFor = computed(() => vehicleId =>
    planning.repoDestNodes(fleet.value.find(v => v.id === vehicleId), allNodes.value))

  return {
    scenarios, current, contracts, myContracts, me, company, fleet, finance, transactions, balanceHistory, vehicleTypes, homeNodes, allNodes, allSegments,
    vehicleEconomics, scheduledRoutes, corridorDemand, corridorState, corridorDetail, corridorDetailLoading,
    fetchScenarios, fetchScenario, fetchContracts, fetchMyContracts, fetchMe,
    fetchFleet, fetchFinance, fetchTransactions, fetchBalanceHistory, fetchVehicleTypes, fetchHomeNodes, fetchAllNodes, fetchAllSegments,
    joinScenario, createScenario, startScenario, updateScenario,
    fetchCompany, saveCompany,
    claimContract, purchaseVehicle, assignVehicle, repositionVehicle, cancelQueuedAssignment,
    fetchScenarioPlayers, adjustPlayerBalance,
    fetchAdminContracts, voidAllContracts, voidSelectedContracts,
    fetchAdminTicks, fetchAdminTickContracts, fetchAdminAudit, fetchAdminRouteAudit,
    fetchAdminPlayers, grantTokens,
    connectWebSocket, disconnectWebSocket,
    gameToRealDate,
    fetchVehicleEconomics,
    fetchScheduledRoutes, createScheduledRoute, updateScheduledRoute, cancelScheduledRoute,
    assignRouteVehicle, removeRouteVehicle, fetchCorridorDemand, fetchCorridorState, fetchVehicleCalendar,
    fetchCorridorDetail, clearCorridorDetail,
    planningMode, openPopoverContext, setPlanningMode, openPopover, closePopover,
    vehicleEligibility, repositionableVehiclesTo, reachableContractsFor, repoDestNodesFor
  }
})
